"use client"

import { useState, useEffect, useRef, useCallback, useMemo } from "react"
import { useGlobalPlaybackStore, type PlaybackSource, type GlobalPlaybackState } from "@/lib/global-playback-store"
import { useSettingsStore, type HandyPreviewPlaybackMode } from "@/lib/settings-store"
import type { UserScript, CommunityScript } from "@/lib/script-store"
import { toast } from "sonner"
import { bindEditorMediaSync } from "@/lib/editor-media-sync"

export type EditorScript = UserScript | CommunityScript | (Partial<UserScript> & { id: string; name: string; funscript: UserScript["funscript"] })

export interface UseEditorPlaybackOptions {
  source: PlaybackSource
  videoRef?: React.RefObject<HTMLVideoElement | null>
}

export interface UseEditorPlaybackReturn {
  /** True when device is playing or local play was requested (for immediate button feedback) */
  isPlaying: boolean
  isLoading: boolean
  isMediaSuspended: () => boolean
  /** Compile script and start playback. Handles setScript + play + video sync. */
  play: (script: EditorScript, opts?: { startTimeSeconds?: number; handyPlaybackMode?: HandyPreviewPlaybackMode }) => Promise<boolean>
  /** Stop playback and reset position */
  stop: () => void
  /** Pause playback without resetting position */
  pause: () => void
  /** Call from video onTimeUpdate — keeps engine in lockstep with video */
  handleTimeUpdate: (timeSeconds: number) => void
  /** Call from seeker interaction — syncs both engine and video */
  handleSeek: (timeSeconds: number) => void
  /** Direct access to store for edge cases */
  globalPlayback: GlobalPlaybackState
}

export function useEditorPlayback({
  source,
  videoRef,
}: UseEditorPlaybackOptions): UseEditorPlaybackReturn {
  // Subscribe only to the two flags that actually affect rendering — not currentTime (4 Hz)
  const deviceIsPlaying = useGlobalPlaybackStore((s) => s.isPlaying)
  const deviceIsLoading = useGlobalPlaybackStore((s) => s.isLoading)
  const syncDelay = useGlobalPlaybackStore((s) => s.syncDelay)
  const setCurrentTime = useGlobalPlaybackStore((s) => s.setCurrentTime)
  const setSyncDelay = useGlobalPlaybackStore((s) => s.setSyncDelay)
  const handyPreviewPlaybackMode = useSettingsStore((state) => state.handyPreviewPlaybackMode)
  const [isPlaying, setIsPlaying] = useState(false)
  const [isStarting, setIsStarting] = useState(false)
  const playRequestRef = useRef(0)
  const mediaSyncRef = useRef<ReturnType<typeof bindEditorMediaSync> | null>(null)
  const isMediaSuspended = useCallback(() => mediaSyncRef.current?.isSuspended() ?? false, [])
  useEffect(() => () => mediaSyncRef.current?.dispose(), [])

  // Track previous device playing state to detect stop transitions
  const prevDevicePlayingRef = useRef(false)

  // When device stops (e.g. script ends, error, stopped by another editor),
  // sync local state so the button reverts to "Play"
  useEffect(() => {
    const wasPlaying = prevDevicePlayingRef.current
    prevDevicePlayingRef.current = deviceIsPlaying

    if (wasPlaying && !deviceIsPlaying && !deviceIsLoading && isPlaying && !mediaSyncRef.current?.isSuspended()) {
      setIsPlaying(false)
      videoRef?.current?.pause()
    }
  }, [deviceIsPlaying, deviceIsLoading, isPlaying, videoRef])

  const play = useCallback(async (script: EditorScript, opts?: { startTimeSeconds?: number; handyPlaybackMode?: HandyPreviewPlaybackMode }) => {
    const request = ++playRequestRef.current
    mediaSyncRef.current?.dispose()
    setIsStarting(true)
    // Use getState() for actions in callbacks — avoids subscribing to the full store
    // If a seek is still in flight the video has no frame yet. Wait for it BEFORE starting the device,
    // so the two start together (starting the device first and the video late is what put them out of
    // sync). Capped at 3s so a stuck video can never block the device.
    const pendingVideo = videoRef?.current
    const requestedTime = opts?.startTimeSeconds ?? 0
    if (pendingVideo && Math.abs(pendingVideo.currentTime - requestedTime) > 0.001) {
      pendingVideo.currentTime = requestedTime
    }
    if (pendingVideo && pendingVideo.seeking) {
      await new Promise<void>((resolve) => {
        let timer: ReturnType<typeof setTimeout>
        const done = () => {
          clearTimeout(timer)
          pendingVideo.removeEventListener("seeked", done)
          pendingVideo.removeEventListener("canplay", done)
          resolve()
        }
        pendingVideo.addEventListener("seeked", done)
        pendingVideo.addEventListener("canplay", done)
        timer = setTimeout(done, 3000)
      })
    }
    if (request !== playRequestRef.current) return false
    const store = useGlobalPlaybackStore.getState()
    if (store.isPlaying || store.isLoading) {
      store.stop()
    }
    store.setActiveSource(source)
    const useInstantPreview = (opts?.handyPlaybackMode ?? handyPreviewPlaybackMode) === "instant"
    store.setScript(script as UserScript, { skipPrewarm: useInstantPreview })

    if (opts?.startTimeSeconds !== undefined) {
      store.setCurrentTime(opts.startTimeSeconds)
    }

    if (pendingVideo && !pendingVideo.loop) {
      mediaSyncRef.current = bindEditorMediaSync(pendingVideo, {
        isPlaying: () => {
          const latest = useGlobalPlaybackStore.getState()
          return request === playRequestRef.current && latest.activeSource === source && latest.isPlaying
        },
        pauseAt: seconds => {
          const latest = useGlobalPlaybackStore.getState()
          latest.pause()
          latest.setCurrentTime(seconds)
        },
        resumeAt: async seconds => {
          if (request !== playRequestRef.current || useGlobalPlaybackStore.getState().activeSource !== source) return
          const latest = useGlobalPlaybackStore.getState()
          latest.setCurrentTime(seconds)
          await latest.play({ skipHSSP: useInstantPreview, beforeStart: async () => {
            if (request !== playRequestRef.current || pendingVideo.paused || pendingVideo.seeking) throw new Error("Media resume canceled")
            return pendingVideo.currentTime * 1000
          } })
        },
        onError: error => console.warn("[EditorPlayback] Media/device resync failed:", error),
      })
    }

    try {
      await store.play({
        skipHSSP: useInstantPreview,
        beforeStart: videoRef?.current ? async () => {
          if (request !== playRequestRef.current) return undefined
          const video = videoRef.current
          if (!video) return undefined
          await video.play()
          return video.currentTime * 1000
        } : undefined,
      })
    } catch (error) {
      if (request !== playRequestRef.current) return false
      setIsStarting(false)
      setIsPlaying(false)
      mediaSyncRef.current?.dispose()
      videoRef?.current?.pause()
      toast.error("Playback failed", { description: error instanceof Error ? error.message : "Could not start device playback" })
      return false
    }

    if (request !== playRequestRef.current) return false
    const latest = useGlobalPlaybackStore.getState()
    setIsStarting(false)
    if (!latest.isPlaying || latest.activeSource !== source) {
      setIsPlaying(false)
      return false
    }
    setIsPlaying(true)

    return true
  }, [handyPreviewPlaybackMode, source, videoRef])

  const stop = useCallback(() => {
    playRequestRef.current++
    mediaSyncRef.current?.dispose()
    setIsStarting(false)
    useGlobalPlaybackStore.getState().stop()
    setIsPlaying(false)
    if (videoRef?.current) {
      videoRef.current.pause()
    }
  }, [videoRef])

  const pause = useCallback(() => {
    playRequestRef.current++
    mediaSyncRef.current?.dispose()
    setIsStarting(false)
    const store = useGlobalPlaybackStore.getState()
    if (store.isLoading) store.stop()
    else store.pause()
    setIsPlaying(false)
    if (videoRef?.current) {
      videoRef.current.pause()
    }
  }, [videoRef])

  const handleTimeUpdate = useCallback((timeSeconds: number) => {
    const store = useGlobalPlaybackStore.getState()
    if (store.isPlaying) {
      store.syncVideoTime(timeSeconds)
    } else {
      store.setCurrentTime(timeSeconds)
    }
  }, [])

  const handleSeek = useCallback((timeSeconds: number) => {
    useGlobalPlaybackStore.getState().setCurrentTime(timeSeconds)
    if (videoRef?.current) {
      videoRef.current.currentTime = timeSeconds
    }
  }, [videoRef])

  const globalPlayback = useMemo(
    () => ({
      ...useGlobalPlaybackStore.getState(),
      isPlaying: deviceIsPlaying,
      isLoading: deviceIsLoading,
      syncDelay,
      setCurrentTime,
      setSyncDelay,
    }),
    [deviceIsLoading, deviceIsPlaying, setCurrentTime, setSyncDelay, syncDelay],
  )

  return {
    isPlaying: deviceIsPlaying || isPlaying,
    isLoading: deviceIsLoading || isStarting,
    isMediaSuspended,
    play,
    stop,
    pause,
    handleTimeUpdate,
    handleSeek,
    globalPlayback,
  }
}
